// "Where you show up" table: postcodes as rows, keywords as columns, built
// from an advertiserOverview() result (its grid). Nothing here counts
// searches - it only arranges the result's grid cells.
//   measure: "any" (ads, map pack or organic) | "ppc" | "local" | "seo"
//   allKeywords: false = the top 6 keywords (compact panel), true = every keyword (expanded)
// Returns { keywords, rows, summary }.
// Each row: { postcode, town, cells: [cell], any: cell, notSeenAdsRan }
// Each cell: { keyword, searches, value, searchesWithAds, status }
// notSeenAdsRan = the postcode's keyword cells (all keywords) not seen where ads ran.

export const GRID_KEYWORD_COLUMNS = 6;

const FIELD = { any: "seenIn", ppc: "ppcSeenIn", local: "localSeenIn", seo: "seoSeenIn" };

const byPostcode = (a, b) => a.postcode.localeCompare(b.postcode, "en-GB", { numeric: true });

// A cell's status for one measure: seen there, not seen where ads ran, or not
// seen with no ads. With measure "any" it equals the grid cell's own status.
export function cellStatus(gridCell, measure = "any") {
  if (gridCell[FIELD[measure]] > 0) return "seen";
  return gridCell.searchesWithAds > 0 ? "not_seen_ads_ran" : "not_seen_no_ads";
}

function toCell(gridCell, keyword, measure) {
  if (!gridCell) return { keyword, searches: 0, value: 0, searchesWithAds: 0, status: "not_seen_no_ads" };
  return {
    keyword, searches: gridCell.searches, value: gridCell[FIELD[measure]],
    searchesWithAds: gridCell.searchesWithAds, status: cellStatus(gridCell, measure),
  };
}

// Default order: postcodes with the most "not seen where ads ran" cells
// first, then the most searches, then postcode A-Z.
export function defaultGridCompare(a, b) {
  return b.notSeenAdsRan - a.notSeenAdsRan || b.any.searches - a.any.searches || byPostcode(a, b);
}

export function advertiserGridTable(result, { measure = "any", allKeywords = false } = {}) {
  const ordered = (result.gridSummary?.keywordsBySearches || []).map(k => k.keyword);
  const keywords = allKeywords ? ordered : ordered.slice(0, GRID_KEYWORD_COLUMNS);

  const byPostcodeKey = new Map();
  for (const g of result.grid || []) {
    if (!byPostcodeKey.has(g.postcode)) byPostcodeKey.set(g.postcode, { postcode: g.postcode, town: g.town, cells: {}, any: null });
    const entry = byPostcodeKey.get(g.postcode);
    if (g.keyword === null) entry.any = g;
    else entry.cells[g.keyword] = g;
  }

  const rows = [...byPostcodeKey.values()].map(entry => {
    const all = ordered.map(k => toCell(entry.cells[k], k, measure)).filter(c => c.searches > 0);
    return {
      postcode: entry.postcode,
      town: entry.town,
      cells: keywords.map(k => toCell(entry.cells[k], k, measure)),
      any: toCell(entry.any, null, measure),
      notSeenAdsRan: all.filter(c => c.status === "not_seen_ads_ran").length,
    };
  });
  rows.sort(defaultGridCompare);

  // Over every keyword cell with completed searches, for this measure.
  const counted = rows.flatMap(r => ordered.map(k => toCell(byPostcodeKey.get(r.postcode).cells[k], k, measure))).filter(c => c.searches > 0);
  const summary = {
    cells: counted.length,
    cellsSeen: counted.filter(c => c.status === "seen").length,
    cellsNotSeenAdsRan: counted.filter(c => c.status === "not_seen_ads_ran").length,
  };
  return { keywords, rows, summary };
}

// Sort columns for the table (see sortRows.js): Postcode, Town, each keyword
// and Any keyword. Keyword columns sort on searches seen in, ties by searches;
// a cell with no completed searches is blank (always at the bottom).
export function gridSortColumns(keywords) {
  const cellValue = cell => (cell.searches === 0 ? null : cell.value * 1e6 + cell.searches);
  const columns = {
    postcode: { value: r => r.postcode, first: "asc" },
    town: { value: r => r.town || null, first: "asc" },
    any: { value: r => cellValue(r.any), first: "desc" },
  };
  keywords.forEach((k, i) => {
    columns[`kw:${k}`] = { value: r => cellValue(r.cells[i]), first: "desc" };
  });
  return columns;
}

// One channel, per keyword, all postcodes in the filters together:
// [{ keyword, searches, seenIn }] - for the expanded channel card. Adds up
// grid cells (each search is in exactly one postcode), most searches first.
// Shopping isn't in the grid, so it has none.
export function channelKeywordTotals(result, channel) {
  const field = FIELD[channel];
  if (!field) return [];
  return (result.gridSummary?.keywordsBySearches || []).map(({ keyword }) => {
    const cells = (result.grid || []).filter(g => g.keyword === keyword);
    return {
      keyword,
      searches: cells.reduce((s, g) => s + g.searches, 0),
      seenIn: cells.reduce((s, g) => s + g[field], 0),
    };
  });
}
